import {
    Accordion,
    Avatar,
    Box,
    Button,
    Grid,
    GridItem,
    HStack,
    Stack,
    Text,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    useColorModeValue,
    Badge,
} from "@chakra-ui/react";
import { Container, Form, Icon, Searchable } from "components";
import { useUser, useWarehouseActiveOrders } from "hooks";
import { useState } from "react";
import { useParams } from "react-router";
import { Warehouse as WarehouseScreens } from "screens";
import { Clock, Package, Truck, Tag } from "lucide-react";

export default function Home() {
    const params = useParams();
    const user = useUser();
    const currentWarehouse = user?.data?.warehouse?.find(w => w.id === params.id);
    const [term, setTerm] = useState();
    const [item, setItem] = useState();
    const [showPendingOrders, setShowPendingOrders] = useState(false);
    const orders = useWarehouseActiveOrders(params.id, term, item);
    const [order, setOrder] = useState();

    // 🕒 Função para calcular "há quanto tempo"
    const timeAgo = (timestamp) => {
        if (!timestamp) return "-";
        const now = new Date();
        const date = new Date(timestamp);
        const diffMs = now - date;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMinutes < 60) return `há ${diffMinutes} min`;
        if (diffHours < 24) return `há ${diffHours} h`;
        return `há ${diffDays} dia${diffDays > 1 ? "s" : ""}`;
    };

    // 🗂️ Filtragem por status
    const waiting = orders?.data?.filter((item) => item.status === 3 && (showPendingOrders ? item.parent_order_id : true)) || [];
    const montage = orders?.data?.filter((item) => item.status === 6 && (showPendingOrders ? item.parent_order_id : true)) || [];
    const quotation = orders?.data?.filter((item) => item.status === 7 && (showPendingOrders ? item.parent_order_id : true)) || [];
    const fiscal = orders?.data?.filter((item) => item.status === 13 && (showPendingOrders ? item.parent_order_id : true)) || [];
    const waiting_shipping = orders?.data?.filter((item) => item.status === 8 && (showPendingOrders ? item.parent_order_id : true)) || [];
    const shipping = orders?.data?.filter((item) => item.status === 9 && (showPendingOrders ? item.parent_order_id : true)) || [];
    const withdraw = orders?.data?.filter((item) => item.status === 11 && (showPendingOrders ? item.parent_order_id : true)) || [];

    const tabColor = useColorModeValue("gray.50", "gray.800");
    const hoverColor = useColorModeValue("gray.100", "gray.700");

    // 🏷️ Cores dos badges
    const badgeColors = {
        status: {
            Aprovado: "green",
            Pendente: "yellow",
            Cancelado: "red",
            default: "gray",
        },
        type: {
            CONSULTOR: "blue",
            NÚCLEO: "purple",
            VOUCHER: "pink",
            default: "gray",
        },
        shipping: {
            Envio: "teal",
            Retirada: "orange",
            default: "gray",
        },
    };

    // 🟡 Renderizador de pedidos
// 🟡 Renderizador de pedidos
    const renderOrders = (data) =>
        data?.length ? (
            data.map((item) => {
                const clientName =
                    item.distributor_to?.name ||
                    item.consultant?.customer?.name ||
                    item.distributor_from?.name ||
                    item.client_name ||
                    "Não informado";

                const valor = (item.total_amount || item.total || 0) / 100;

                const shippingLabel = item.shipping_type_label || "-";
                const statusLabel = item.status_label || "Sem status";
                const typeLabel = item.type_label || "-";

                const colorShipping =
                    badgeColors.shipping[shippingLabel] || badgeColors.shipping.default;
                const colorStatus =
                    badgeColors.status[statusLabel] || badgeColors.status.default;
                const colorType =
                    badgeColors.type[typeLabel] || badgeColors.type.default;

                return (
                    <HStack
                        key={item.id}
                        justify="space-between"
                        p={4}
                        borderWidth="1px"
                        borderColor="gray.600"
                        borderRadius="md"
                        bg={tabColor}
                        _hover={{ bg: hoverColor, cursor: "pointer" }}
                        mb={2}
                        onClick={() => setOrder(item)}
                    >
                        {/* Ícone e código */}
                        <HStack spacing={4}>
                            <Icon as={Package} color="gray.400" />
                            <Text fontWeight="bold">
                                Pedido #{item.cod || item.code || item.id}
                            </Text>
                        </HStack>

                        {/* Nome do cliente + badges de status e tipo */}
                        <HStack flex="1" ml={6} spacing={2} align="center" minW="200px">
                            <Text fontSize="sm" noOfLines={1}>
                                <strong>{clientName}</strong>
                            </Text>
                            <Badge colorScheme={colorStatus}>{statusLabel}</Badge>
                            <Badge colorScheme={colorType}>{typeLabel}</Badge>
                            <Badge colorScheme={colorShipping}>{shippingLabel}</Badge>
                        </HStack>

                        {/* Valor */}
                        <Text
                            fontWeight="semibold"
                            color="green.300"
                            minW="110px"
                            textAlign="right"
                        >
                            R${" "}
                            {valor.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </Text>

                        {/* Tipo de envio e tempo */}
                        <HStack spacing={2} color="gray.400" minW="130px" justify="flex-end">
                            <Icon as={Clock} />
                            <Text fontSize="sm">
                                {timeAgo(item.requested_at || item.created_at)}
                            </Text>
                        </HStack>
                    </HStack>
                );
            })
        ) : (
            <Text color="gray.400" textAlign="center" mt={4}>
                Nenhum pedido nesta etapa.
            </Text>
        );


    return (
        <Container>

            {/* 🧑‍💼 Cabeçalho */}
            <Grid templateColumns="repeat(3, 1fr)" gap={8} mb={3} alignItems="center">
                <GridItem>
                    <HStack>
                        <Avatar name={user?.data?.name} />
                        <Box>
                            <Text
                                as="span"
                                fontWeight="bold"
                                textTransform="capitalize"
                                children={user?.data?.name}
                            />
                            <Text fontSize="sm" children={user?.data?.tagline} />
                        </Box>
                    </HStack>
                </GridItem>

                <GridItem>
                    <Box>
                        <Text fontSize="sm">Centro de distribuição:</Text>
                        <Text
                            as="span"
                            fontWeight="bold"
                            textTransform="capitalize"
                        >
                            {currentWarehouse?.name ?? '—'}
                        </Text>
                    </Box>
                </GridItem>

                <GridItem textAlign="right">
                    <Button
                        leftIcon={<Icon as={Clock} />}
                        onClick={orders.refetch}
                        isLoading={orders.isFetching}
                    >
                        Atualizar pedidos
                    </Button>
                </GridItem>
            </Grid>

            <HStack>
                <Box w="65%">
                    <Searchable value={term} setValue={setTerm} placeholder="Pesquisar por ID, Nome do cliente ou Código do pedido...">
                    </Searchable>
                </Box>

                <Box w="35%">
                    <Searchable value={item} setValue={setItem} placeholder="Pesquisar por Produto do pedido..."></Searchable>
                </Box>
            </HStack>

            <HStack w="100%" mt={3} justify="end" align="center">
                <Text fontSize="sm" whiteSpace="nowrap">Filtrar Pedidos de Pendência</Text>
                <Box>
                    <Form.Switch value={showPendingOrders} onChange={e => setShowPendingOrders(e.target.checked)}/>
                </Box>
            </HStack>

            {/* 🗂️ Abas de status */}
            <Tabs variant="enclosed" colorScheme="yellow" isFitted mt={3}>
                <TabList mb="1em">
                    <Tab _selected={{ color: "yellow.400", borderColor: "yellow.400" }}>
                        <HStack>
                            <Icon as={Clock} />
                            <Text>Aguardando ({waiting.length})</Text>
                        </HStack>
                    </Tab>
                    <Tab _selected={{ color: "orange.400", borderColor: "orange.400" }}>
                        <HStack>
                            <Icon as={Package} />
                            <Text>Montagem ({montage.length})</Text>
                        </HStack>
                    </Tab>
                    <Tab _selected={{ color: "blue.400", borderColor: "blue.400" }}>
                        <HStack>
                            <Icon as={Tag} />
                            <Text>Cotação ({quotation.length})</Text>
                        </HStack>
                    </Tab>
                    <Tab _selected={{ color: "pink.400", borderColor: "pink.400" }}>
                        <HStack>
                            <Icon as={Truck} />
                            <Text>Nota Fiscal ({fiscal.length})</Text>
                        </HStack>
                    </Tab>
                    <Tab _selected={{ color: "teal.400", borderColor: "teal.400" }}>
                        <HStack>
                            <Icon as={Truck} />
                            <Text>Aguardando Coleta ({waiting_shipping.length})</Text>
                        </HStack>
                    </Tab>
                    <Tab _selected={{ color: "yellow.300", borderColor: "yellow.300" }}>
                        <HStack>
                            <Icon as={Truck} />
                            <Text>Em Trânsito ({shipping.length})</Text>
                        </HStack>
                    </Tab>
                    <Tab _selected={{ color: "cyan.400", borderColor: "cyan.400" }}>
                        <HStack>
                            <Icon as={Truck} />
                            <Text>Aguardando Retirada ({withdraw.length})</Text>
                        </HStack>
                    </Tab>
                </TabList>

                <TabPanels>
                    <TabPanel>{renderOrders(waiting)}</TabPanel>
                    <TabPanel>{renderOrders(montage)}</TabPanel>
                    <TabPanel>{renderOrders(quotation)}</TabPanel>
                    <TabPanel>{renderOrders(fiscal)}</TabPanel>
                    <TabPanel>{renderOrders(waiting_shipping)}</TabPanel>
                    <TabPanel>{renderOrders(shipping)}</TabPanel>
                    <TabPanel>{renderOrders(withdraw)}</TabPanel>
                </TabPanels>
            </Tabs>

            {/* 🧾 Modal de Pedido */}
            <WarehouseScreens.Order
                isOpen={!!order}
                data={order}
                onClose={() => setOrder(undefined)}
            />
        </Container>
    );
}
